import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BRAND_NAME, MEMBERSHIP_NAME } from "@/constants/brand";
import { MEMBERSHIP_PRICE } from "@/lib/membership";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  IndianRupee,
  ShieldCheck,
  Sparkles,
  CheckCircle2,
  Loader2,
  QrCode,
  Wallet,
} from "lucide-react";
import MembershipCardPreview from "@/components/MembershipCardPreview";
import SuccessCard from "@/components/SuccessCard";
import { api } from "@/lib/api";
import {
  formatMobile,
  getValidityRange,
  validateMobile,
  validateName,
} from "@/lib/membership";

const STEPS = [
  { id: 1, label: "Your details" },
  { id: 2, label: "Payment" },
  { id: 3, label: "Card ready" },
];


export default function Generate() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [gender, setGender] = useState("");
  const [age, setAge] = useState("");
  const [emrStation, setEmrStation] = useState("");
  const [mobile, setMobile] = useState("");
  const [whatsappOptIn, setWhatsappOptIn] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [member, setMember] = useState(null);
  const [qr, setQr] = useState(null);

  const validity = useMemo(() => getValidityRange(), []);

  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [step]);

  const handleDetailsSubmit = (e) => {
    e.preventDefault();
    if (!validateName(name)) {
      toast.error("Please enter a valid full name");
      return;
    }
    if (!gender) {
      toast.error("Please select gender");
      return;
    }
    if (!age || parseInt(age) < 1) {
      toast.error("Please enter a valid age");
      return;
    }
    if (!validateMobile(mobile)) {
      toast.error("Enter a valid 10-digit Indian mobile number");
      return;
    }
    setStep(2);
  };

  const fmtDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  const handlePayment = async () => {
    if (processing) return;
    setProcessing(true);
    try {
      const res = await api.post("/public/create-qr", {
        patient_name: name.trim(),
        patient_mobile: mobile.replace(/\D/g, ""),
        whatsapp_optin: whatsappOptIn,
        gender,
        age: parseInt(age),
        emr_station: emrStation.trim() || undefined,
      });
      setQr(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start payment. Try again.");
      setProcessing(false);
    }
  };

  // Wallet payment — Razorpay Checkout popup (UPI/Card/Netbanking/Wallets).
  const handleCheckout = async () => {
    if (processing) return;
    setProcessing(true);
    try {
      const orderRes = await api.post("/public/create-order", {
        patient_name: name.trim(),
        patient_mobile: mobile.replace(/\D/g, ""),
        whatsapp_optin: whatsappOptIn,
        gender,
        age: parseInt(age),
        emr_station: emrStation.trim() || undefined,
      });
      const order = orderRes.data;
      await new Promise((resolve, reject) => {
        const rzp = new window.Razorpay({
          key: order.key_id,
          order_id: order.order_id,
          amount: order.amount,
          currency: order.currency,
          name: "1Rupee Clinic",
          description: "Annual Membership",
          prefill: { name: order.patient_name, contact: "+91" + order.patient_mobile },
          theme: { color: "#4f46e5" },
          handler: async (response) => {
            try {
              const verifyRes = await api.post("/public/verify-payment", {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              });
              const data = verifyRes.data;
              setMember({
                name: data.patient_name,
                mobile: data.patient_mobile,
                membershipNumber: data.membership_number,
                issued: fmtDate(data.issued_at),
                expires: fmtDate(data.expires_at),
                issuedAt: data.issued_at,
                paymentId: response.razorpay_payment_id,
              });
              setStep(3);
              toast.success("Payment successful — your card is ready!");
              resolve();
            } catch (err) {
              toast.error(err?.response?.data?.detail || "Verification failed.");
              reject(err);
            } finally {
              setProcessing(false);
            }
          },
          modal: { ondismiss: () => { setProcessing(false); resolve(); } },
        });
        rzp.open();
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not initiate payment. Try again.");
      setProcessing(false);
    }
  };

  const cancelQr = () => {
    setQr(null);
    setProcessing(false);
  };

  // Poll QR payment status; on payment show the card (step 3).
  useEffect(() => {
    if (!qr?.qr_id) return;
    let active = true;
    const startedAt = Date.now();
    const iv = setInterval(async () => {
      if (!active) return;
      if (Date.now() - startedAt > 8 * 60 * 1000) {
        clearInterval(iv);
        if (active) {
          toast.error("QR expired. Please try again.");
          setQr(null);
          setProcessing(false);
        }
        return;
      }
      try {
        const res = await api.post("/public/qr-status", { qr_id: qr.qr_id });
        if (res.data?.paid) {
          active = false;
          clearInterval(iv);
          const data = res.data;
          setMember({
            name: data.patient_name,
            mobile: data.patient_mobile,
            membershipNumber: data.membership_number,
            issued: fmtDate(data.issued_at),
            expires: fmtDate(data.expires_at),
            issuedAt: data.issued_at,
          });
          setQr(null);
          setStep(3);
          setProcessing(false);
          toast.success("Payment successful — your card is ready!");
        }
      } catch {
        /* transient — keep polling */
      }
    }, 4000);
    return () => {
      active = false;
      clearInterval(iv);
    };
  }, [qr]);

  return (
    <main className="relative min-h-screen bg-gradient-soft">
      {qr && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-card rounded-2xl shadow-elegant max-w-md w-full p-5 text-center max-h-[88vh] overflow-y-auto">
            <h2 className="font-display text-lg font-bold text-foreground mb-3">
              Scan &amp; Pay ₹{MEMBERSHIP_PRICE}
            </h2>
            <img
              src={qr.qr_image_url}
              alt="UPI QR code"
              className="mx-auto w-[300px] max-w-full h-auto rounded-lg bg-white"
            />
            <div className="mt-4 flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Waiting for payment…
            </div>
            <Button variant="outline" className="mt-4 w-full rounded-xl" onClick={cancelQr}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      <Navbar />
      <div className="absolute inset-0 grid-pattern opacity-40 pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 pt-28 pb-20">
        <div className="flex items-center justify-between gap-3 mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
          <Badge variant="secondary" className="bg-background/80 border border-border">
            <ShieldCheck className="h-3.5 w-3.5 mr-1.5 text-success" />
            Secure checkout
          </Badge>
        </div>

        {/* Stepper */}
        <ol className="flex items-center justify-center gap-2 sm:gap-4 mb-10">
          {STEPS.map((s, i) => {
            const active = step === s.id;
            const done = step > s.id;
            return (
              <li key={s.id} className="flex items-center gap-2 sm:gap-4">
                <div className={"flex items-center gap-2.5 " + (done || active ? "text-foreground" : "text-muted-foreground")}>
                  <span
                    className={
                      "inline-flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold border transition-all " +
                      (done
                        ? "bg-success text-success-foreground border-success"
                        : active
                        ? "bg-gradient-primary text-primary-foreground border-transparent shadow-elegant"
                        : "bg-background text-muted-foreground border-border")
                    }
                  >
                    {done ? <CheckCircle2 className="h-4 w-4" /> : s.id}
                  </span>
                  <span className="text-xs sm:text-sm font-medium hidden sm:inline">{s.label}</span>
                </div>
                {i < STEPS.length - 1 && (
                  <span className={"h-px w-8 sm:w-16 " + (done ? "bg-success" : "bg-border")} />
                )}
              </li>
            );
          })}
        </ol>

        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3">
            {step === 1 && (
              <Card className="border-border/70 shadow-soft">
                <CardContent className="p-6 sm:p-8">
                  <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
                    Let’s create your card
                  </h1>
                  <p className="mt-1.5 text-sm text-muted-foreground">
                    Just two details. Your card will be sent to the same mobile number on WhatsApp.
                  </p>

                  <form onSubmit={handleDetailsSubmit} className="mt-7 space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full name (as per ID)</Label>
                      <Input
                        id="name"
                        placeholder="e.g. Aarav Sharma"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="h-11 bg-background"
                        autoComplete="name"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="mobile">Mobile number</Label>
                      <div className="flex items-stretch rounded-md border border-input bg-background overflow-hidden focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background">
                        <span className="flex items-center px-3 text-sm font-medium bg-muted/60 text-muted-foreground border-r border-border">
                          +91
                        </span>
                        <Input
                          id="mobile"
                          inputMode="numeric"
                          placeholder="98765 43210"
                          value={formatMobile(mobile)}
                          onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                          className="h-11 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 rounded-none bg-transparent"
                          autoComplete="tel-national"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        We’ll send the card image to this WhatsApp number after payment.
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Gender *</Label>
                        <Select value={gender} onValueChange={setGender}>
                          <SelectTrigger className="h-11 bg-background">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">Male</SelectItem>
                            <SelectItem value="2">Female</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="age">Age *</Label>
                        <Input
                          id="age"
                          type="text"
                          inputMode="numeric"
                          placeholder="e.g. 35"
                          value={age}
                          onChange={(e) => setAge(e.target.value.replace(/\D/g, "").slice(0, 3))}
                          className="h-11 bg-background"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="emr_station">EMR Station <span className="text-muted-foreground font-normal">(Optional)</span></Label>
                      <Input
                        id="emr_station"
                        placeholder="e.g. Andheri, Kandivali"
                        value={emrStation}
                        onChange={(e) => setEmrStation(e.target.value)}
                        maxLength={50}
                        className="h-11 bg-background"
                      />
                    </div>

                    {/* WhatsApp/SMS opt-in hidden — defaults to opted-in.
                    <label className="flex items-start gap-3 rounded-xl border border-border/70 bg-muted/40 p-3.5 cursor-pointer hover:bg-muted/70 transition-colors">
                      <Checkbox
                        checked={whatsappOptIn}
                        onCheckedChange={(v) => setWhatsappOptIn(Boolean(v))}
                        className="mt-0.5"
                      />
                      <span className="text-sm text-foreground">
                        Send my card to WhatsApp & SMS receipt
                        <span className="block text-xs text-muted-foreground mt-0.5">
                          You can also download the card directly from the next screen.
                        </span>
                      </span>
                    </label>
                    */}

                    <div className="pt-2 flex items-center justify-between">
                      <div className="text-sm">
                        <div className="font-semibold text-foreground">Total ₹{MEMBERSHIP_PRICE}.00</div>
                        <div className="text-xs text-muted-foreground">Annual membership · incl. taxes</div>
                      </div>
                      <Button type="submit" size="lg" className="h-11 bg-gradient-primary text-primary-foreground shadow-elegant rounded-xl">
                        Continue to payment <ArrowRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            )}

            {step === 2 && (
              <Card className="border-border/70 shadow-soft">
                <CardContent className="p-6 sm:p-8">
                  <div className="flex items-center justify-between">
                    <div>
                      <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
                        Complete payment
                      </h1>
                      <p className="mt-1.5 text-sm text-muted-foreground">
                        Powered by Razorpay · PCI-DSS Level 1 secured
                      </p>
                    </div>
                    <img
                      src="https://cdn.brandfetch.io/idVtFp5RWX/theme/dark/symbol.svg?c=1bxid64Mup7aczewSAYMX&t=1738591033528"
                      alt="Razorpay"
                      className="h-7 opacity-70"
                      onError={(e) => (e.currentTarget.style.display = "none")}
                    />
                  </div>

                  <div className="mt-6 rounded-2xl bg-gradient-primary text-primary-foreground p-5 flex items-center justify-between">
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.22em] opacity-80">Amount to pay</div>
                      <div className="flex items-end gap-1 mt-1">
                        <IndianRupee className="h-7 w-7 mb-1" />
                        <span className="font-display font-bold text-4xl leading-none">{MEMBERSHIP_PRICE}</span>
                        <span className="text-sm opacity-80 mb-1">.00</span>
                      </div>
                      <div className="text-xs opacity-80 mt-1">Annual membership · 1 year validity</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[11px] uppercase tracking-[0.22em] opacity-80">For</div>
                      <div className="font-semibold text-sm sm:text-base">{name || "—"}</div>
                      <div className="text-xs opacity-80">+91 {formatMobile(mobile)}</div>
                    </div>
                  </div>

                  {/* Static "Online Payment / UPI, Card, Netbanking & Wallets via Razorpay" card hidden.
                  <div className="mt-6">
                    <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground mb-3">
                      Payment method
                    </div>
                    <div className="rounded-xl border border-primary bg-primary/5 ring-2 ring-primary/20 p-4 flex items-center gap-3">
                      <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-primary text-primary-foreground">
                        <IndianRupee className="h-5 w-5" />
                      </span>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-foreground">Online Payment</div>
                        <div className="text-xs text-muted-foreground">UPI, Card, Netbanking & Wallets via Razorpay</div>
                      </div>
                      <span className="ml-auto h-4 w-4 rounded-full border-2 border-primary bg-primary" />
                    </div>
                  </div>
                  */}

                  <div className="mt-6 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground mb-3">
                    Payment method
                  </div>
                  <div className="space-y-3">
                    <button
                      type="button"
                      onClick={handlePayment}
                      disabled={processing}
                      className="w-full flex items-center gap-3 rounded-xl border-2 border-primary/40 bg-primary/5 hover:bg-primary/10 p-4 text-left transition disabled:opacity-60"
                    >
                      <QrCode className="h-6 w-6 text-primary shrink-0" />
                      <span className="flex-1">
                        <span className="block font-semibold text-foreground">Online Payment (QR)</span>
                        <span className="block text-xs text-muted-foreground">
                          Scan &amp; pay ₹{MEMBERSHIP_PRICE} with any UPI app
                        </span>
                      </span>
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    </button>
                    <button
                      type="button"
                      onClick={handleCheckout}
                      disabled={processing}
                      className="w-full flex items-center gap-3 rounded-xl border-2 border-border hover:bg-muted/40 p-4 text-left transition disabled:opacity-60"
                    >
                      <Wallet className="h-6 w-6 text-primary shrink-0" />
                      <span className="flex-1">
                        <span className="block font-semibold text-foreground">Wallet Payment</span>
                        <span className="block text-xs text-muted-foreground">
                          UPI, Card, Netbanking &amp; Wallets via Razorpay
                        </span>
                      </span>
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    </button>
                    <div className="flex items-center justify-between pt-1">
                      <Button variant="ghost" size="sm" onClick={() => setStep(1)} disabled={processing}>
                        <ArrowLeft className="h-4 w-4" /> Edit details
                      </Button>
                      {processing && (
                        <span className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Processing…
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
                    <ShieldCheck className="h-3.5 w-3.5 text-success" />
                    Your payment details are never stored on our servers.
                  </div>
                </CardContent>
              </Card>
            )}

            {step === 3 && member && (
              <SuccessCard member={member} onReset={() => navigate("/")} />
            )}
          </div>

          {/* Sidebar summary / card preview */}
          <aside className="lg:col-span-2 space-y-5">
            <div className="rounded-2xl border border-border/70 bg-card p-5 shadow-soft">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground mb-3">
                Order summary
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground">{MEMBERSHIP_NAME}</span>
                <span className="font-semibold text-foreground">₹{MEMBERSHIP_PRICE}.00</span>
              </div>
              <div className="flex items-center justify-between text-xs text-muted-foreground mt-1.5">
                <span>Valid {validity.issued} → {validity.expires}</span>
                <span>1 year</span>
              </div>
              <Separator className="my-4" />
              <div className="flex items-center justify-between text-base font-semibold">
                <span>Total</span>
                <span className="text-primary">₹{MEMBERSHIP_PRICE}.00</span>
              </div>
            </div>

            <div className="rounded-2xl border border-dashed border-primary/30 bg-background/60 p-5">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground mb-3">
                Live card preview
              </div>
              <MembershipCardPreview
                name={name || "Your name"}
                mobile={mobile || "9999999999"}
                membershipNumber={member?.membershipNumber || "XXXXXXXX"}
                issued={validity.issued}
                expires={validity.expires}
              />
              <p className="mt-4 text-xs text-muted-foreground leading-relaxed">
                Your unique alphanumeric membership number is auto-generated upon successful
                payment and locked to your name & mobile.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}
